import React, { useEffect, useState } from 'react';
import { BasicModal } from '@DesignSystem/modals';
import { TextInput, Checkbox } from '@ds';

import styles from '../ViewSelect.module.scss';
import { isEqual } from 'lodash';

export type FormViewModalProps = {
  title: string;
  isOpened: boolean;
  onClose: () => void;
  onConfirm: (templateName: string, isDefault: boolean) => void;
  isLoading: boolean;
  primaryButtonText: string;
  formData: {
    name: string;
    isDefault: boolean;
  };
  isEditMode: boolean;
  existedNames: string[];
};
const FormViewModal: React.FC<FormViewModalProps> = ({
  isOpened,
  onClose,
  onConfirm,
  isLoading,
  title,
  primaryButtonText,
  formData,
  isEditMode,
  existedNames
}) => {
  const [name, setName] = useState(formData.name);
  const [isDefault, setIsDefault] = useState(formData.isDefault);

  const nameIsAlreadyExisted = existedNames.some(viewName => name === viewName);

  const onDefaultToggle = () => {
    setIsDefault(prev => !prev);
  };

  const isValid = (() => {
    if (isEditMode) {
      return (
        Boolean(name) &&
        !nameIsAlreadyExisted &&
        !isEqual(formData, {
          name,
          isDefault
        })
      );
    }

    return Boolean(name) && !nameIsAlreadyExisted;
  })();

  const onSubmit = () => {
    if (!isValid) return;

    onConfirm(name, isDefault);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;

    onSubmit();
  };

  useEffect(() => {
    if (!isOpened) return;

    setIsDefault(formData.isDefault);
    setName(formData.name);
  }, [isOpened, formData.isDefault, formData.name]);

  const handleClose = () => {
    if (isLoading) return;

    onClose();
  };

  return (
    <BasicModal
      open={isOpened}
      onClose={handleClose}
      title={title}
      onKeyDown={onKeyDown}
      content={
        <div className={styles.formContainer}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Name of the view</span>
            <TextInput
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter name"
              maxLength={100}
              autoFocus
              data-test="view-select-name-field"
              invalid={!isLoading && (!name || nameIsAlreadyExisted)}
              helperText={
                nameIsAlreadyExisted && !isLoading && isOpened
                  ? 'This name already exists, please choose a different one'
                  : ''
              }
            />
          </div>
          <div className={styles.toggle}>
            <Checkbox
              id="view-default-toggle"
              checked={isDefault}
              onChange={onDefaultToggle}
            />
            <label
              htmlFor="view-default-toggle"
              data-test="view-select-default-field"
              data-value={isDefault}
            >
              Set as default
            </label>
          </div>
        </div>
      }
      primaryButtonText={primaryButtonText}
      secondaryButtonText="Cancel"
      onSecondaryButtonClick={handleClose}
      onPrimaryButtonClick={onSubmit}
      isPrimaryButtonDisabled={isLoading || !isValid}
      isPrimaryBtnLoading={isLoading}
    />
  );
};

export default FormViewModal;
